<template>
  <div class="mc-icon-tooltip">
    <span
      :id="`${id}-tooltip`"
      :class="triggerClass"
      @mouseenter="isHover = true"
      @mouseleave="isHover = false"
    >
      <i :class="iconClass" :style="iconStyle" />
    </span>
    <b-tooltip
      v-if="showTooltip"
      :target="`${id}-tooltip`"
      :show="forceShow"
      :triggers="forceShow ? 'manual' : 'hover'"
      :placement="placement"
      :custom-class="computedTooltipCustomClass"
    >
      <slot name="content">
        <div v-if="hasStructuredTooltip" class="mc-icon-tooltip__content">
          <div v-if="tooltipTitle" class="mc-icon-tooltip__title">{{ tooltipTitle }}</div>
          <div
            v-for="(item, index) in tooltipItems"
            :key="`${id}-tooltip-item-${index}`"
            class="mc-icon-tooltip__row"
          >
            <span class="mc-icon-tooltip__label">{{ item.label }}: </span>
            <span class="mc-icon-tooltip__value">{{ item.value }}</span>
          </div>
        </div>
        <template v-else>
          {{ tooltip }}
        </template>
      </slot>
    </b-tooltip>
  </div>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    tooltipTitle: {
      type: String,
      default: '',
    },
    tooltipItems: {
      type: Array,
      default: () => [],
    },
    tooltipCustomClass: {
      type: String,
      default: '',
    },
    placement: {
      type: String,
      default: 'right',
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (v) =>
        [
          'primary',
          'secondary',
          'success',
          'danger',
          'warning',
          'info',
          'light',
          'dark',
          'multi-info',
        ].includes(v),
    },
    icon: {
      type: String,
      default: '',
    },
    iconSize: {
      type: [Number, String],
      default: 20,
    },
    showTooltip: {
      type: Boolean,
      default: true,
    },
    forceShow: {
      type: Boolean,
      default: false,
    },
    withBackground: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isHover: false,
    }
  },
  computed: {
    iconClass() {
      return this.icon
    },
    variantClass() {
      return `tooltip-variant-${this.variant}`
    },
    triggerClass() {
      return [
        'mc-icon-tooltip__trigger',
        this.variantClass,
        {
          'tooltip-disabled': !this.showTooltip,
          'mc-icon-tooltip__trigger--background':
            this.withBackground || this.variant === 'multi-info',
        },
      ]
    },
    iconStyle() {
      return {
        fontSize: `${this.iconSize}px`,
      }
    },
    hasStructuredTooltip() {
      return this.tooltipTitle || this.tooltipItems.length > 0
    },
    computedTooltipCustomClass() {
      return [
        this.hasStructuredTooltip ? 'mc-icon-tooltip__tooltip' : '',
        this.hasStructuredTooltip ? `mc-icon-tooltip__tooltip--${this.variant}` : '',
        this.tooltipCustomClass,
      ]
        .filter(Boolean)
        .join(' ')
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-icon-tooltip {
  margin-right: 5px;
  display: inline-flex;

  &__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    cursor: pointer;

    &.tooltip-disabled {
      cursor: default;
    }

    &--background {
      width: 32px;
      height: 32px;
      border-radius: 50%;
    }

    i {
      display: inline-flex;
      font-size: 20px;

      &::before {
        font-size: inherit;
      }
    }

    &.tooltip-variant-primary {
      i::before {
        color: $blue;
      }

      &.mc-icon-tooltip__trigger--background {
        background-color: $blue-100;
      }
    }

    &.tooltip-variant-warning {
      i::before {
        color: $yellow;
      }

      &.mc-icon-tooltip__trigger--background {
        background-color: $yellow-100;
      }
    }

    &.tooltip-variant-danger {
      i::before {
        color: $red;
      }

      &.mc-icon-tooltip__trigger--background {
        background-color: $red-100;
      }
    }

    &.tooltip-variant-success {
      i::before {
        color: $green;
      }

      &.mc-icon-tooltip__trigger--background {
        background-color: $green-100;
      }
    }

    &.tooltip-variant-multi-info {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background-color: $yellow-100;

      i::before {
        color: $yellow;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: $white;
  }

  &__title {
    font-size: 14px;
    font-weight: $font-weight-bold;
    line-height: 1.35;
  }

  &__row {
    font-size: 12px;
    line-height: 1.45;
  }

  &__label {
    font-weight: $font-weight-bold;
  }

  &__value {
    font-size: 12px;
  }
}
</style>

<style lang="scss">
.mc-icon-tooltip__tooltip {
  .arrow {
    display: none;
  }

  .tooltip-inner {
    text-align: left;
    opacity: 1;
  }
}

.mc-icon-tooltip__tooltip--multi-info {
  .tooltip-inner {
    min-width: 250px;
    max-width: 250px;
    padding: 10px;
    border-radius: 5px;
    background-color: #434c67;
  }
}
</style>
